/* ── IdentifiedGroupList ────────────────────────────────────────────────
   Left-hand panel displaying all identified directory groups and accounts
   that have not yet been moved to Privileged or Non-privileged.
   Features search, type filter (Groups / Accounts), multi-selection,
   drag-and-drop, and pagination.
   ──────────────────────────────────────────────────────────────────────── */

// External Libraries
import {
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Search,
  SlidersHorizontal,
  User,
  Users,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

// Internal Modules
import { SearchInput } from "../../../components/SearchInput";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { cn } from "../../../components/ui/utils";

// Relative Imports
import type { UnifiedGroup } from "../constants/pagAccountConfigConstants";

export type IdentifiedTypeFilter = "ALL" | "GROUP" | "ACCOUNT";

export interface IdentifiedGroupListProps {
  groups: UnifiedGroup[];
  totalCount?: number | null;
  editing: boolean;
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onDropToIdentified?: (e: React.DragEvent) => void;
  dragOver?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
}

const ITEMS_PER_PAGE = 8;
const MAX_PAGES_WITHOUT_ELLIPSIS = 7;

/* ── Pure Helpers ───────────────────────────────────────────────────── */

/** Check if a unified group matches a case-insensitive search term across attributes. */
function matchesSearchTerm(group: UnifiedGroup, term: string): boolean {
  if (!term) return true;
  return (
    group.groupName.toLowerCase().includes(term) ||
    (group.userName ?? "").toLowerCase().includes(term) ||
    (group.displayName ?? "").toLowerCase().includes(term) ||
    (group.email ?? "").toLowerCase().includes(term) ||
    (group.description ?? "").toLowerCase().includes(term) ||
    (group.sourceSystemName ?? "").toLowerCase().includes(term)
  );
}

/** Filter unified groups by keyword search and group/account classification. */
function filterIdentifiedGroups(
  groups: UnifiedGroup[],
  search: string,
  typeFilter: IdentifiedTypeFilter,
): UnifiedGroup[] {
  const term = search.trim().toLowerCase();
  return groups.filter((g) => {
    if (typeFilter !== "ALL" && g.itemType !== typeFilter) {
      return false;
    }
    return matchesSearchTerm(g, term);
  });
}

/** Generate pagination window with ellipsis for large page sets. */
function generatePageNumbers(
  totalPages: number,
  currentPage: number,
): (number | string)[] {
  if (totalPages <= MAX_PAGES_WITHOUT_ELLIPSIS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [
      1,
      "...",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

/** Format account or group count subtitle text. */
function getItemSubtitle(group: UnifiedGroup, isAccount: boolean): string {
  if (isAccount) {
    return group.userName ? `@${group.userName}` : "Account";
  }
  const count = group.accountCount ?? 0;
  return `${count} ${count === 1 ? "account" : "accounts"}`;
}

/* ── Subcomponents ──────────────────────────────────────────────────── */

interface IdentifiedHeaderProps {
  totalCount?: number | null;
  groupsCount: number;
}

function IdentifiedHeader({ totalCount, groupsCount }: IdentifiedHeaderProps) {
  const displayCount = typeof totalCount === "number" ? totalCount : groupsCount;

  return (
    <div className="flex items-center justify-between pb-3">
      <div className="flex items-center gap-2">
        <div className="flex size-8 items-center justify-center rounded-xl bg-blue-500/15 text-blue-400">
          <Users className="size-4" />
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <h2 className="text-sm font-bold text-foreground">
              Identified Groups / Accounts
            </h2>
            <span className="text-xs font-semibold text-muted-foreground tabular-nums">
              ({displayCount})
            </span>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground/70 hover:text-foreground transition-colors"
                    aria-label="Info about identified groups and accounts"
                  >
                    <HelpCircle className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  Discovered directory groups and accounts awaiting classification. Drag or
                  select items to categorize them as Privileged or Non-privileged.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
}

interface IdentifiedTypeFilterPopoverProps {
  typeFilter: IdentifiedTypeFilter;
  showFilterMenu: boolean;
  setShowFilterMenu: React.Dispatch<React.SetStateAction<boolean>>;
  filterContainerRef: React.RefObject<HTMLDivElement | null>;
  onSelectFilter: (filter: IdentifiedTypeFilter) => void;
}

function IdentifiedTypeFilterPopover({
  typeFilter,
  showFilterMenu,
  setShowFilterMenu,
  filterContainerRef,
  onSelectFilter,
}: IdentifiedTypeFilterPopoverProps) {
  return (
    <div ref={filterContainerRef} className="relative">
      <button
        type="button"
        onClick={() => setShowFilterMenu((prev) => !prev)}
        title="Filter by type"
        className={cn(
          "flex size-9 items-center justify-center rounded-lg border border-border/70 bg-background/60 text-muted-foreground transition-colors hover:border-blue-500/50 hover:text-foreground",
          typeFilter !== "ALL" && "border-blue-500 bg-blue-500/10 text-blue-400",
        )}
      >
        <SlidersHorizontal className="size-3.5" />
      </button>

      {showFilterMenu && (
        <div className="absolute right-0 top-11 z-20 w-44 rounded-xl border border-border bg-popover p-1.5 shadow-lg">
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Filter by Type
          </div>
          <button
            type="button"
            onClick={() => onSelectFilter("ALL")}
            className={cn(
              "w-full rounded-md px-2 py-1 text-left text-xs transition-colors hover:bg-muted/60",
              typeFilter === "ALL" && "bg-blue-500/15 font-semibold text-blue-400",
            )}
          >
            All Types
          </button>
          <button
            type="button"
            onClick={() => onSelectFilter("GROUP")}
            className={cn(
              "w-full rounded-md px-2 py-1 text-left text-xs transition-colors hover:bg-muted/60",
              typeFilter === "GROUP" && "bg-blue-500/15 font-semibold text-blue-400",
            )}
          >
            Groups Only
          </button>
          <button
            type="button"
            onClick={() => onSelectFilter("ACCOUNT")}
            className={cn(
              "w-full rounded-md px-2 py-1 text-left text-xs transition-colors hover:bg-muted/60",
              typeFilter === "ACCOUNT" && "bg-blue-500/15 font-semibold text-blue-400",
            )}
          >
            Accounts Only
          </button>
        </div>
      )}
    </div>
  );
}

interface IdentifiedEmptyStateProps {
  totalInitialCount: number;
  search: string;
  typeFilter: IdentifiedTypeFilter;
  onResetFilter: () => void;
}

function IdentifiedEmptyState({
  totalInitialCount,
  search,
  typeFilter,
  onResetFilter,
}: IdentifiedEmptyStateProps) {
  if (totalInitialCount === 0) {
    return (
      <div className="m-auto flex flex-col items-center justify-center py-14 px-4 text-center">
        <div className="mb-2.5 flex size-10 items-center justify-center rounded-xl bg-muted/40 text-muted-foreground">
          <Users className="size-5" />
        </div>
        <p className="text-xs font-semibold text-foreground">No Data Available</p>
        <p className="mt-1 max-w-[210px] text-[11px] leading-relaxed text-muted-foreground">
          There are currently no identified groups or accounts to display.
        </p>
      </div>
    );
  }

  const filterDescription = search
    ? `No items match "${search}".`
    : `No ${typeFilter === "GROUP" ? "groups" : "accounts"} found for this filter.`;

  return (
    <div className="m-auto flex flex-col items-center justify-center py-14 px-4 text-center">
      <div className="mb-2.5 flex size-10 items-center justify-center rounded-xl bg-muted/40 text-muted-foreground">
        <Search className="size-5" />
      </div>
      <p className="text-xs font-semibold text-foreground">No Data Available</p>
      <p className="mt-1 max-w-[210px] text-[11px] leading-relaxed text-muted-foreground">
        {filterDescription}
      </p>
      <button
        type="button"
        onClick={onResetFilter}
        className="mt-2.5 text-xs font-medium text-blue-400 hover:text-blue-300 hover:underline"
      >
        Clear filter
      </button>
    </div>
  );
}

interface IdentifiedGroupCardProps {
  group: UnifiedGroup;
  editing: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, group: UnifiedGroup) => void;
}

function IdentifiedGroupCard({
  group,
  editing,
  isSelected,
  onToggleSelect,
  onDragStart,
}: IdentifiedGroupCardProps) {
  const isAccount = group.itemType === "ACCOUNT";
  const subtitle = getItemSubtitle(group, isAccount);

  return (
    <div
      draggable={editing}
      onDragStart={(e) => onDragStart(e, group)}
      className={cn(
        "group relative flex items-center justify-between gap-2.5 rounded-xl border p-2.5 transition-all text-xs",
        isSelected
          ? "border-blue-500/70 bg-blue-500/15 ring-1 ring-blue-500/40 shadow-sm"
          : "border-border/60 bg-background/50 hover:border-border hover:bg-muted/30",
        editing && "cursor-grab active:cursor-grabbing",
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {editing && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(group.id)}
            aria-label={`Select ${group.groupName}`}
            className="size-4 shrink-0 rounded border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
          />
        )}
        <div
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-full",
            isAccount
              ? "bg-indigo-500/15 text-indigo-400"
              : "bg-blue-500/10 text-blue-400",
          )}
        >
          {isAccount ? <User className="size-3.5" /> : <Users className="size-3.5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-xs font-semibold leading-4 text-foreground"
            title={group.groupName}
          >
            {group.groupName}
          </p>
          <div className="flex items-center gap-1.5 pt-0.5 text-[10px] text-muted-foreground">
            <span>{subtitle}</span>
            {group.sourceSystemName && <span>• {group.sourceSystemName}</span>}
          </div>
        </div>
      </div>

      {isAccount && (
        <div className="flex items-center gap-1 shrink-0">
          <span className="rounded-md border border-indigo-500/30 bg-indigo-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-300">
            Account
          </span>
        </div>
      )}
    </div>
  );
}

interface IdentifiedPaginationProps {
  safeCurrentPage: number;
  totalPages: number;
  pageNumbers: (number | string)[];
  onPageChange: (page: number) => void;
}

function IdentifiedPagination({
  safeCurrentPage,
  totalPages,
  pageNumbers,
  onPageChange,
}: IdentifiedPaginationProps) {
  return (
    <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-3">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, safeCurrentPage - 1))}
        disabled={safeCurrentPage === 1}
        className="flex size-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:border-border hover:text-foreground disabled:opacity-40 disabled:hover:border-border/60 disabled:hover:text-muted-foreground"
        aria-label="Previous page"
      >
        <ChevronLeft className="size-3.5" />
      </button>

      <div className="flex items-center gap-1">
        {pageNumbers.map((pageNum, idx) => {
          if (pageNum === "...") {
            return (
              <span
                key={`ellipsis-${idx}`}
                className="flex size-7 items-center justify-center text-xs text-muted-foreground"
              >
                ...
              </span>
            );
          }
          const isActive = pageNum === safeCurrentPage;
          return (
            <button
              key={`page-${pageNum}`}
              type="button"
              onClick={() => onPageChange(Number(pageNum))}
              className={cn(
                "flex size-7 items-center justify-center rounded-lg text-xs font-medium transition-colors",
                isActive
                  ? "bg-blue-600 font-semibold text-white shadow-xs"
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, safeCurrentPage + 1))}
        disabled={safeCurrentPage === totalPages}
        className="flex size-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:border-border hover:text-foreground disabled:opacity-40 disabled:hover:border-border/60 disabled:hover:text-muted-foreground"
        aria-label="Next page"
      >
        <ChevronRight className="size-3.5" />
      </button>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────── */

export const IdentifiedGroupList = ({
  groups,
  totalCount,
  editing,
  selectedIds,
  onToggleSelect,
  onDropToIdentified,
  dragOver = false,
  onDragOver,
  onDragLeave,
}: IdentifiedGroupListProps) => {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<IdentifiedTypeFilter>("ALL");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterContainerRef = useRef<HTMLDivElement>(null);

  // Close filter popover when clicking outside
  useEffect(() => {
    if (!showFilterMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(event.target as Node)
      ) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterMenu]);

  // Filter groups by search & type
  const filteredGroups = useMemo(
    () => filterIdentifiedGroups(groups, appliedSearch, typeFilter),
    [groups, appliedSearch, typeFilter],
  );

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedGroups = useMemo(() => {
    const start = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
    return filteredGroups.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredGroups, safeCurrentPage]);

  // Generate page numbers with ellipsis window
  const pageNumbers = useMemo(
    () => generatePageNumbers(totalPages, safeCurrentPage),
    [totalPages, safeCurrentPage],
  );

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, group: UnifiedGroup) => {
    const ids = selectedIds.includes(group.id) ? selectedIds : [group.id];
    e.dataTransfer.setData("text/plain", ids.join(","));
  };

  const handleSelectFilter = (filter: IdentifiedTypeFilter) => {
    setTypeFilter(filter);
    setCurrentPage(1);
    setShowFilterMenu(false);
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (!val.trim()) {
      setAppliedSearch("");
      setCurrentPage(1);
    }
  };

  const handleSearchSubmit = (val: string) => {
    setAppliedSearch(val.trim());
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    setAppliedSearch("");
    setSearch("");
    setTypeFilter("ALL");
    setCurrentPage(1);
  };

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDropToIdentified}
      className={cn(
        "flex w-full flex-col rounded-2xl border border-border/80 bg-card/70 p-4 shadow-sm backdrop-blur-sm lg:w-[320px] xl:w-[340px] shrink-0 transition-colors",
        dragOver && "border-blue-400 bg-blue-500/10 ring-2 ring-blue-500/40",
      )}
    >
      <IdentifiedHeader totalCount={totalCount} groupsCount={groups.length} />

      {/* ── Search and Filter Controls ── */}
      <div className="relative mb-3 flex items-center gap-2">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
          placeholder="Search"
          size="md"
          className="flex-1"
        />
        <IdentifiedTypeFilterPopover
          typeFilter={typeFilter}
          showFilterMenu={showFilterMenu}
          setShowFilterMenu={setShowFilterMenu}
          filterContainerRef={filterContainerRef}
          onSelectFilter={handleSelectFilter}
        />
      </div>

      {/* ── Group & Account List ── */}
      <div className="flex min-h-[460px] flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
        {paginatedGroups.length === 0 ? (
          <IdentifiedEmptyState
            totalInitialCount={groups.length}
            search={appliedSearch}
            typeFilter={typeFilter}
            onResetFilter={handleResetFilter}
          />
        ) : (
          paginatedGroups.map((group) => (
            <IdentifiedGroupCard
              key={group.id}
              group={group}
              editing={editing}
              isSelected={selectedIds.includes(group.id)}
              onToggleSelect={onToggleSelect}
              onDragStart={handleDragStart}
            />
          ))
        )}
      </div>

      {/* ── Pagination Controls ── */}
      {totalPages > 1 && (
        <IdentifiedPagination
          safeCurrentPage={safeCurrentPage}
          totalPages={totalPages}
          pageNumbers={pageNumbers}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default IdentifiedGroupList;
